/**
 * Trajectory Solver — finds (angle, power) pairs that hit a target.
 *
 * Adapted from rendering/trajectory.ts. Simulates projectile flight
 * with gravity, wind, terrain collision, and bounces. Evaluates many
 * (angle, power) pairs, scores by blast radius proximity to target.
 *
 * Two-pass approach:
 *   1. Coarse sweep: angleSamples × powerSamples (skill-dependent grid)
 *   2. Refinement: 5×5 fine sweep centered on best coarse result
 *
 * Budget: ~288 coarse + 25 refine = ~313 sims × ~300 steps ≈ 94K iterations. Under 1ms.
 *
 * Uses seeded RNG (not Math.random) — AI decisions affect game state.
 */

import { WORLD, PHYSICS, AIM } from '../constants';
import { isPointInTerrain } from '../terrain/grid';
import { random } from '../terrain/rng';
import type { WeaponDef } from '../weapons';
import type { SkillProfile } from './skill-profile';

export interface AimSolution {
  angle: number;  // radians — turret angle (game convention: PI/2 = up)
  power: number;  // 0-1 normalized
  score: number;  // quality — higher = better (0 = miss)
}

// ── Scoring constants ──────────────────────────────────────────

const SELF_DAMAGE_WEIGHT = 0.5;
const VOID_PROXIMITY_RANGE = 300;
const VOID_BONUS_WEIGHT = 0.6;
const REFINE_SAMPLES = 5;

/** Nonlinear power curve — matches aiming.ts chargeToPower(). */
function chargeToPower(linearCharge: number): number {
  return linearCharge * (0.4 + 0.6 * linearCharge);
}

/** Gaussian-ish noise via Box-Muller transform. Uses seeded RNG. */
function gaussianNoise(stddev: number): number {
  const u1 = random();
  const u2 = random();
  return stddev * Math.sqrt(-2 * Math.log(Math.max(u1, 1e-10))) * Math.cos(2 * Math.PI * u2);
}

/**
 * Score an impact point relative to a target.
 * Accounts for blast proximity, self-damage penalty, and void proximity bonus.
 */
function scoreImpact(
  impactX: number, impactY: number,
  targetX: number, targetY: number,
  shooterX: number, shooterY: number,
  blastRadius: number,
  voidY: number,
): number {
  const dx = impactX - targetX;
  const dy = impactY - targetY;
  const targetDist = Math.sqrt(dx * dx + dy * dy);

  // Base score: within blast radius = positive, farther = negative
  let score = blastRadius - targetDist;

  // Self-damage penalty: deduct if impact is near the shooter
  const sdx = impactX - shooterX;
  const sdy = impactY - shooterY;
  const shooterDist = Math.sqrt(sdx * sdx + sdy * sdy);
  score -= Math.max(0, blastRadius - shooterDist) * SELF_DAMAGE_WEIGHT;

  // Void proximity bonus: prefer shots that land above targets near the void
  // (knockback pushes target downward toward void)
  if (targetDist < blastRadius) {
    const distToVoid = voidY - targetY;
    if (distToVoid > 0 && distToVoid < VOID_PROXIMITY_RANGE && impactY < targetY) {
      const voidProximity = Math.max(0, 1 - distToVoid / VOID_PROXIMITY_RANGE);
      score += voidProximity * VOID_BONUS_WEIGHT * blastRadius;
    }
  }

  return score;
}

/**
 * Simulate one projectile flight and return the impact point.
 * Returns null if projectile flies off-world without hitting terrain.
 */
function simulateShot(
  startX: number, startY: number,
  vx: number, vy: number,
  weapon: WeaponDef,
  wind: number,
): { x: number; y: number } | null {
  let x = startX, y = startY;
  let cvx = vx, cvy = vy;
  let bouncesRemaining = weapon.bounces;

  for (let step = 0; step < 300; step++) {
    cvy += weapon.gravity;
    if (wind !== 0) cvx += wind;
    x += cvx;
    y += cvy;

    // Out of bounds
    if (x < WORLD.WORLD_LEFT || x > WORLD.WORLD_RIGHT) {
      // Wall bounce
      cvx *= -0.9;
      x = x < WORLD.WORLD_LEFT ? WORLD.WORLD_LEFT : WORLD.WORLD_RIGHT;
    }
    if (y < WORLD.WORLD_TOP) {
      cvy *= -0.9;
      y = WORLD.WORLD_TOP;
    }

    // Below void — lost
    if (y > WORLD.VIRTUAL_HEIGHT + 200) return null;

    // Terrain collision
    if (isPointInTerrain(x, y, true)) {
      if (bouncesRemaining > 0) {
        // Bounce — simple reflect upward
        cvy = -Math.abs(cvy) * (weapon.bounceEnergyRetention ?? 0.7);
        cvx *= (weapon.bounceEnergyRetention ?? 0.7);
        // Push out
        for (let i = 0; i < 10; i++) {
          y -= 2;
          if (!isPointInTerrain(x, y, true)) break;
        }
        bouncesRemaining--;
        continue;
      }
      return { x, y };
    }
  }
  return null; // flew off without hitting
}

/**
 * Simulate a single (angle, power) pair and return its score.
 * Returns -Infinity if the shot doesn't impact terrain.
 */
function evaluateShot(
  angle: number, power: number,
  shooterX: number, shooterY: number,
  targetX: number, targetY: number,
  weapon: WeaponDef, wind: number,
  blastRadius: number, turretLength: number,
  voidY: number,
): { score: number; angle: number; power: number } {
  const fireAngle = -angle; // screen space
  const speed = chargeToPower(power) * PHYSICS.MAX_POWER * weapon.projectileSpeed;
  const vx = Math.cos(fireAngle) * speed;
  const vy = Math.sin(fireAngle) * speed;

  const sx = shooterX + Math.cos(fireAngle) * turretLength;
  const sy = shooterY + Math.sin(fireAngle) * turretLength;

  const impact = simulateShot(sx, sy, vx, vy, weapon, wind);
  if (!impact) return { score: -Infinity, angle, power };

  const score = scoreImpact(
    impact.x, impact.y,
    targetX, targetY,
    shooterX, shooterY,
    blastRadius, voidY,
  );
  return { score, angle, power };
}

/**
 * Find the best (angle, power) to hit a target position.
 * Two-pass: coarse sweep → refinement around best result.
 */
export function solveTrajectory(
  shooterX: number,
  shooterY: number,
  targetX: number,
  targetY: number,
  weapon: WeaponDef,
  wind: number,
  profile: SkillProfile,
  voidY: number,
): AimSolution {
  const turretLength = 56; // TANK_VISUAL.TURRET_LENGTH
  const blastRadius = weapon.blastRadius;

  let bestAngle = Math.PI / 2; // default: straight up
  let bestPower = 0.5;
  let bestScore = -Infinity;

  const { angleSamples, powerSamples } = profile;

  // Sweep angle range (game convention: angle in [-PI/4, 5PI/4])
  const minAngle = AIM.MIN_ANGLE;
  const maxAngle = AIM.MAX_ANGLE;

  const angleStep = (maxAngle - minAngle) / (angleSamples - 1);
  const powerStep = 0.85 / (powerSamples - 1);

  // ── Pass 1: Coarse sweep ──────────────────────────────────────

  for (let ai = 0; ai < angleSamples; ai++) {
    const angle = minAngle + angleStep * ai;

    for (let pi = 0; pi < powerSamples; pi++) {
      const power = 0.15 + powerStep * pi;

      const result = evaluateShot(
        angle, power,
        shooterX, shooterY,
        targetX, targetY,
        weapon, wind,
        blastRadius, turretLength,
        voidY,
      );
      if (result.score > bestScore) {
        bestScore = result.score;
        bestAngle = result.angle;
        bestPower = result.power;
      }
    }
  }

  // ── Pass 2: Refinement (5×5 around best coarse result) ────────

  if (bestScore > -Infinity) {
    const refineAngleHalf = angleStep;
    const refinePowerHalf = powerStep;

    for (let ai = 0; ai < REFINE_SAMPLES; ai++) {
      const angle = Math.max(minAngle, Math.min(maxAngle,
        bestAngle - refineAngleHalf + (2 * refineAngleHalf) * (ai / (REFINE_SAMPLES - 1)),
      ));

      for (let pi = 0; pi < REFINE_SAMPLES; pi++) {
        const power = Math.max(0.15, Math.min(1.0,
          bestPower - refinePowerHalf + (2 * refinePowerHalf) * (pi / (REFINE_SAMPLES - 1)),
        ));

        const result = evaluateShot(
          angle, power,
          shooterX, shooterY,
          targetX, targetY,
          weapon, wind,
          blastRadius, turretLength,
          voidY,
        );
        if (result.score > bestScore) {
          bestScore = result.score;
          bestAngle = result.angle;
          bestPower = result.power;
        }
      }
    }
  }

  // Apply gaussian noise based on skill
  const noisyAngle = bestAngle + gaussianNoise(profile.aimNoise);
  const noisyPower = Math.max(0.1, Math.min(1.0, bestPower + gaussianNoise(profile.powerNoise)));

  // Clamp angle to valid range
  const clampedAngle = Math.max(minAngle, Math.min(maxAngle, noisyAngle));

  return {
    angle: clampedAngle,
    power: noisyPower,
    score: bestScore,
  };
}
